import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { GB_SPACE_CONDITIONS, LEASE_TYPES, US_SPACE_CONDITIONS, StoreState } from '@root/types';
import { useSelector } from 'react-redux';
import {
  Select,
  CurrencyInput,
  NumberInput,
  TextArea,
  DateInput,
  Checkbox,
  RadioInput,
} from '@components/shared/forms';
import { useFormikContext } from 'formik';
import classnames from 'classnames';
import orderSpaceConditions from '@root/shared/orderSpaceConditions';
import { countryToCurrencyCode } from '@root/shared/currencyByCountry';
import { FormValues } from '.';
import styles from './TourbookExternalListing.module.less';
import { askingRentInMoneyPerAreaPerPeriod, askingRentInMoneyPerPeriod } from './helpers';

type RefFn = (node: Element | undefined | null) => void;
type Props = {
  containerRef?: RefFn | null;
};

export const ListingFields = ({ containerRef = null }: Props) => {
  const { t } = useTranslation('tourbook');
  const {
    values: { availableImmediately, askingRent, size, askingRentUnitsWithoutCurrency, countryCode },
  } = useFormikContext<FormValues>();

  const currentUser = useSelector((state: StoreState) => state.currentUser);
  const currency = countryToCurrencyCode(countryCode);
  const spaceConditions = countryCode === 'GB' ? GB_SPACE_CONDITIONS : US_SPACE_CONDITIONS;
  const orderedSpaceConditions = orderSpaceConditions([...spaceConditions], t);

  const oppositeUnits = (): ReactNode => {
    if (!askingRent || !size || size <= 0) return null;

    let convertedAskingRent: string;

    if (askingRentUnitsWithoutCurrency === 'Mo') {
      const magnitude = askingRentInMoneyPerAreaPerPeriod(
        askingRent,
        askingRentUnitsWithoutCurrency,
        size,
        currency,
      )!.magnitude!;

      convertedAskingRent = t('units:pricePerAreaPerPeriod', {
        pricePerAreaPerPeriod: {
          magnitude: Math.round(magnitude),
          area: 'sf',
          currency,
          period: 'yr',
          abbreviatedPeriod: true,
        },
      });
    } else {
      const magnitude = askingRentInMoneyPerPeriod(
        askingRent,
        askingRentUnitsWithoutCurrency as 'sf/yr',
        size,
        currency,
      )!.magnitude!;

      convertedAskingRent = t('units:pricePerPeriod', {
        pricePerPeriod: {
          magnitude: Math.round(magnitude),
          currency,
          period: 'mo',
          abbreviatedPeriod: true,
        },
      });
    }

    return <span className={classnames(styles.convertedAskingRent)}>{convertedAskingRent}</span>;
  };

  const formattedAskingRentUnitsWithoutCurrency = (): string =>
    askingRentUnitsWithoutCurrency === 'Mo' ? '/ mo' : 'sf / yr';

  return (
    <section ref={containerRef} id="listing-fields">
      <div className={classnames(styles.locationLabel, styles.bottomMargin)}>
        <span>{t('externalListing.listing')}</span>
      </div>
      <NumberInput
        name="size"
        required
        placeholder={t('externalListing.sizePlaceholder')}
        labelText={t('externalListing.sizeLabel')}
        autoComplete={false}
        units="sf"
      />
      <CurrencyInput
        name="askingRent"
        labelText={t('externalListing.rentLabel')}
        autoComplete={false}
        currency={currency}
        locale={currentUser?.languagePreference || 'en-us'}
        displayUnit={formattedAskingRentUnitsWithoutCurrency()}
      >
        <ul className={styles.units}>
          <li>
            <RadioInput
              name="askingRentUnitsWithoutCurrency"
              value="sf/yr"
              labelText="sf / yr"
              id="asking-rent-sf/yr"
            />
          </li>
          <li>
            <RadioInput
              name="askingRentUnitsWithoutCurrency"
              value="Mo"
              labelText="mo"
              id="asking-rent-mo"
            />
          </li>
          <li>{askingRent && size ? oppositeUnits() : null}</li>
        </ul>
      </CurrencyInput>
      <DateInput
        name="availabilityDate"
        labelText={t('externalListing.available')}
        placeholder={t('externalListing.blankDate')}
        required
        disabled={availableImmediately}
        autoComplete="none"
      >
        <Checkbox labelClassName={styles.availableNowCheckbox} name="availableImmediately">
          {t('externalListing.availableNow')}
        </Checkbox>
      </DateInput>
      <Select
        required
        labelText={t('externalListing.condition')}
        name="condition"
        isClearable={false}
        options={orderedSpaceConditions.map(spaceCondition => ({
          label: t(`spaceConditions:${spaceCondition}`),
          value: spaceCondition,
        }))}
      />
      <Select
        required
        labelText={t('externalListing.leaseTypeLabel')}
        name="leaseType"
        isClearable={false}
        options={LEASE_TYPES.map(leaseType => ({
          label: t(`admin:listing.leaseTypes.${leaseType}`),
          value: leaseType,
        }))}
      />
      <TextArea
        className={styles.textArea}
        labelText={t('externalListing.description')}
        placeholder={t('externalListing.description')}
        name="description"
      />
    </section>
  );
};

export default ListingFields;
